import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { FadeUp, Eyebrow } from "@/components/reveal";

const SECTORS = [
    {
        name: "Ports & terminals",
        desc: "Container terminals, freight stations, and port-led development.",
    },
    {
        name: "Maritime & shipping",
        desc: "Fleets, maritime technology, and the decarbonisation of shipping.",
    },
    {
        name: "Logistics",
        desc: "Inland logistics parks, warehousing, and multimodal networks.",
    },
    {
        name: "Cold chain",
        desc: "Temperature-controlled storage and distribution for food and pharma.",
    },
    {
        name: "Pharma supply chains",
        desc: "Manufacturing and distribution capacity for global markets.",
    },
    {
        name: "Energy transition",
        desc: "Clean fuels, power-to-X, subsea, and critical-mineral infrastructure.",
    },
];

const BAND_IMG =
    "https://images.unsplash.com/photo-1634638021403-70f46d19fc02?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA6MTJ8MHwxfHNlYXJjaHwzfHxjb250YWluZXIlMjBwb3J0JTIwY29udGFpbmVyJTIwc2hpcCUyMGNhcmdvJTIwaW5mcmFzdHJ1Y3R1cmUlMjBtYXJpdGltZSUyMHNoaXBwaW5nJTIwbG9naXN0aWNzJTIwZ3JlZW4lMjBlbmVyZ3klMjB0cmFuc2l0aW9uJTIwdGVybWluYWwlMjB3YXJlaG91c2UlMjBwb3J0JTIwY3JhbmV8ZW58MHx8fHwxNzkxNTMyMjQyfDA&ixlib=rb-4.1.0&q=85";

const Sectors = () => {
    const band = useRef(null);
    const { scrollYProgress } = useScroll({
        target: band,
        offset: ["start end", "end start"],
    });
    const y = useTransform(scrollYProgress, [0, 1], ["-10%", "10%"]);

    return (
        <section id="sectors" data-testid="sectors-section" className="py-24 lg:py-32">
            <div className="container-x">
                <div className="grid gap-10 lg:grid-cols-12">
                    <div className="lg:col-span-7">
                        <FadeUp>
                            <Eyebrow>Sectors</Eyebrow>
                        </FadeUp>
                        <FadeUp delay={0.1}>
                            <h2 className="mt-6 font-serif text-4xl tracking-tight sm:text-5xl lg:text-6xl">
                                Infrastructure,{" "}
                                <em className="italic text-olive">globally</em>
                            </h2>
                        </FadeUp>
                    </div>
                    <div className="lg:col-span-4 lg:col-start-9">
                        <FadeUp delay={0.2}>
                            <p className="text-lg leading-relaxed text-inksoft">
                                We work across infrastructure as a whole, with
                                deepest expertise in the systems that move goods and
                                energy around the world.
                            </p>
                        </FadeUp>
                    </div>
                </div>

                <div className="mt-16 grid gap-px overflow-hidden border border-ink/10 bg-ink/10 sm:grid-cols-2 lg:grid-cols-3">
                    {SECTORS.map((s, i) => (
                        <FadeUp key={s.name} delay={i * 0.06} className="h-full">
                            <div
                                data-testid={`sector-card-${i + 1}`}
                                className="group relative h-full bg-paper p-8 transition-colors duration-500 hover:bg-olive-soft"
                            >
                                <span className="font-mono text-[11px] tracking-[0.2em] text-inkmut">
                                    {String(i + 1).padStart(2, "0")}
                                </span>
                                <h3 className="mt-10 font-serif text-2xl text-ink">
                                    {s.name}
                                </h3>
                                <p className="mt-3 text-sm leading-relaxed text-inksoft">
                                    {s.desc}
                                </p>
                                <ArrowUpRight className="absolute right-6 top-6 h-4 w-4 text-olive opacity-0 transition-opacity duration-300 group-hover:opacity-100" />
                            </div>
                        </FadeUp>
                    ))}
                </div>
            </div>

            <div ref={band} className="container-x mt-20">
                <div
                    data-testid="sectors-image-band"
                    className="relative h-[46vh] overflow-hidden lg:h-[64vh]"
                >
                    <motion.img
                        src={BAND_IMG}
                        alt="Container port logistics at scale"
                        style={{ y }}
                        className="h-[120%] w-full object-cover"
                    />
                    <div
                        className="absolute inset-0 bg-olive/15 mix-blend-multiply"
                        aria-hidden="true"
                    />
                </div>
            </div>
        </section>
    );
};

export default Sectors;
